/**
 * ITER283 Phase 4 — Global HTTP 423 (Access Denied) handler.
 *
 * Whenever the backend AccessGuard refuses access to a module (account
 * under_review / suspended / deleted, or per-cell matrix denial), it
 * returns HTTP 423 with a structured payload:
 *
 *   {
 *     code: "ACCESS_DENIED_UNDER_REVIEW" | "ACCESS_DENIED_SUSPENDED" | ...,
 *     message_key: "access_guard.under_review",
 *     status: "under_review",
 *     module: "withdrawals",
 *     support_email: "contact@japapmessenger.com",
 *     support_mailto: "mailto:contact@japapmessenger.com",
 *     message: "Votre compte fait actuellement l'objet…"  // FR fallback
 *   }
 *
 * This module installs a global axios interceptor + a matching fetch
 * hook that:
 *   1. Captures 423 responses anywhere in the SPA.
 *   2. Emits a `japap:access-denied` CustomEvent carrying the payload.
 *   3. `AccessDeniedListener` (mounted at the app root) picks it up and
 *      displays a humanised, localised toast with a « Contacter le
 *      support » action button.
 *
 * The message is NEVER hardcoded in the components consuming the API —
 * every module benefits automatically as soon as the guard is wired.
 */
import axios from 'axios';

const EVENT_NAME = 'japap:access-denied';

function extractDetail(data) {
  const detail = data?.detail;
  if (detail && typeof detail === 'object') return detail;
  return null;
}

function fire(detail) {
  if (typeof window === 'undefined' || !window.dispatchEvent) return;
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
  } catch (_) { /* SSR / old browsers */ }
}

// ── Axios interceptor (piggy-backs on existing error chain) ────────────
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    if (status === 423) {
      const detail = extractDetail(error.response?.data);
      if (detail) fire(detail);
    }
    return Promise.reject(error);
  },
);

// ── fetch hook (only fires the event, does not modify the response) ────
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const _origFetch = window.fetch;
  window.fetch = async function fetchWithAccessGuardHook(input, init) {
    const res = await _origFetch(input, init);
    if (res && res.status === 423) {
      try {
        // Clone so consumers can still parse the body themselves.
        const clone = res.clone();
        const body = await clone.json().catch(() => null);
        const detail = extractDetail(body);
        if (detail) fire(detail);
      } catch (_) { /* ignore */ }
    }
    return res;
  };
}

export const ACCESS_DENIED_EVENT = EVENT_NAME;
